import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  BULK_ORDER_GRID_AXES,
  formatPowerLabel,
  getDisplayLinePowers,
  isBulkOrderSideEnabled,
  selectGridContext,
} from '../../common/bulkOrder'
import {
  RecapRoot,
  RecapHeader,
  RecapSummary,
  RecapEmpty,
  RecapGroupList,
  RecapGroup,
  RecapGroupTitle,
  RecapLineList,
  RecapLineButton,
  RecapLineLabel,
  RecapLineQty,
} from './BulkOrderProductRecapPanel.styles'
import BulkOrderCountSummary from './BulkOrderCountSummary'
import { BulkOrderSectionLabel } from './bulkOrderSectionLabel.styles'

const BulkOrderProductRecapPanel = ({
  groups,
  lineCount,
  totalQuantity,
  gridAxis = BULK_ORDER_GRID_AXES.CYLINDER,
  onLineClick,
}) => {
  const { t } = useTranslation()
  const { cylinderSign } = useSelector(selectGridContext)
  const showSide = isBulkOrderSideEnabled()

  return (
    <RecapRoot>
      <RecapHeader>
        <BulkOrderSectionLabel>{t('Selected items')}</BulkOrderSectionLabel>
        {lineCount > 0 && (
          <RecapSummary>
            <BulkOrderCountSummary
              variantCount={lineCount}
              pieceCount={totalQuantity}
            />
          </RecapSummary>
        )}
      </RecapHeader>

      {lineCount === 0 
        ? <RecapEmpty>{t('no items selected')}</RecapEmpty>
       : (
        <RecapGroupList>
          {groups.map(group => (
            <RecapGroup key={group.diameter}>
              <RecapGroupTitle>Ø {group.diameter}</RecapGroupTitle>
              <RecapLineList>
                {group.lines.map(line => {
                  const lineGridAxis = line.gridAxis || gridAxis
                  const display = getDisplayLinePowers(line, lineGridAxis, cylinderSign)
                  return (
                    <RecapLineButton
                      key={line.id}
                      type="button"
                      onClick={() => onLineClick(line)}
                    >
                      <RecapLineLabel>
                        {showSide && `${line.side} · `}
                        {formatPowerLabel(display.sphere)} /{' '}
                        {formatPowerLabel(display.columnValue)}
                      </RecapLineLabel>
                      <RecapLineQty>× {line.quantity}</RecapLineQty>
                    </RecapLineButton>
                  )
                })}
              </RecapLineList>
            </RecapGroup>
          ))}
        </RecapGroupList>
      )}
    </RecapRoot>
  )
}

BulkOrderProductRecapPanel.propTypes = {
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      diameter: PropTypes.number.isRequired,
      lineCount: PropTypes.number.isRequired,
      totalQuantity: PropTypes.number.isRequired,
      lines: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          diameter: PropTypes.number.isRequired,
          side: PropTypes.string.isRequired,
          sphere: PropTypes.number.isRequired,
          cylinder: PropTypes.number.isRequired,
          addition: PropTypes.number,
          quantity: PropTypes.number.isRequired,
        }),
      ).isRequired,
    }),
  ).isRequired,
  lineCount: PropTypes.number.isRequired,
  totalQuantity: PropTypes.number.isRequired,
  gridAxis: PropTypes.oneOf(Object.values(BULK_ORDER_GRID_AXES)),
  onLineClick: PropTypes.func.isRequired,
}

export default BulkOrderProductRecapPanel
